import * as React from "react"
import {
  BoxesIcon,
  CalendarDaysIcon,
  ClipboardListIcon,
  ClockIcon,
  HandHeartIcon,
  HeartHandshakeIcon,
  PackageIcon,
  UsersIcon,
  WalletIcon,
  type LucideIcon,
} from "lucide-react"

import { SearchResultKind } from "@ibada/shared-types/search"

import { SearchNavKind, type SearchEntryKind } from "@/features/search/model/search-palette-types"
import { cn } from "@/shared/lib/utils"

const KIND_ICONS: Record<SearchResultKind, LucideIcon> = {
  [SearchResultKind.Member]: UsersIcon,
  [SearchResultKind.Transaction]: WalletIcon,
  [SearchResultKind.Pledge]: HeartHandshakeIcon,
  [SearchResultKind.Activity]: CalendarDaysIcon,
  [SearchResultKind.Group]: UsersIcon,
  [SearchResultKind.Ministry]: HandHeartIcon,
  [SearchResultKind.Department]: BoxesIcon,
  [SearchResultKind.Form]: ClipboardListIcon,
  [SearchResultKind.Asset]: PackageIcon,
}

const FINANCE_TILE = "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
const ACTIVITY_TILE = "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
const FORM_TILE = "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
const NEUTRAL_TILE = "bg-muted text-foreground/75"

const KIND_TILES: Record<SearchResultKind, string> = {
  [SearchResultKind.Member]: NEUTRAL_TILE,
  [SearchResultKind.Transaction]: FINANCE_TILE,
  [SearchResultKind.Pledge]: FINANCE_TILE,
  [SearchResultKind.Activity]: ACTIVITY_TILE,
  [SearchResultKind.Group]: NEUTRAL_TILE,
  [SearchResultKind.Ministry]: NEUTRAL_TILE,
  [SearchResultKind.Department]: NEUTRAL_TILE,
  [SearchResultKind.Form]: FORM_TILE,
  [SearchResultKind.Asset]: NEUTRAL_TILE,
}

function isResultKind(kind: SearchEntryKind): kind is SearchResultKind {
  return kind !== SearchNavKind.Page && kind !== SearchNavKind.Action
}

/** The square icon tile that leads a non-member row (and heads its preview). */
export function SearchKindTile({
  kind,
  icon,
  recent = false,
  className,
}: {
  kind: SearchEntryKind
  /** Explicit icon (pages and actions bring their own). */
  icon?: React.ReactNode
  recent?: boolean
  className?: string
}) {
  const Icon = recent ? ClockIcon : isResultKind(kind) ? KIND_ICONS[kind] : null
  const tint = !recent && kind === SearchNavKind.Action ? FINANCE_TILE : !recent && isResultKind(kind) ? KIND_TILES[kind] : NEUTRAL_TILE
  return (
    <span
      aria-hidden
      className={cn("inline-flex size-8 shrink-0 items-center justify-center rounded-[9px] [&_svg]:size-4", tint, className)}
    >
      {Icon ? <Icon /> : icon}
    </span>
  )
}
